import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Upload, FileText, CheckCircle, AlertTriangle, RefreshCw, Trash2,
  Server, Globe, Cpu, Layers, Loader, X
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status, reachable }) {
  if (reachable) {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-400/20 bg-emerald-400/10 text-emerald-400 text-[10px] font-semibold uppercase">
        <CheckCircle size={10} /> Connected
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-amber-400/20 bg-amber-400/10 text-amber-400 text-[10px] font-semibold uppercase">
      <AlertTriangle size={10} /> Unreachable
    </span>
  );
}

function ClusterCard({ cluster, onRefresh, onDelete, refreshing }) {
  return (
    <div data-testid="connected-cluster-card" className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-md bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center flex-shrink-0">
            <Server size={16} className="text-indigo-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-100 text-sm truncate">{cluster.name}</p>
            <p className="text-xs text-slate-500 font-mono truncate mt-0.5 flex items-center gap-1">
              <Globe size={10} /> {cluster.region || "unknown"}
            </p>
          </div>
        </div>
        <StatusBadge status={cluster.status} reachable={cluster.reachable} />
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-800/60">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">K8s</p>
          <p className="text-sm font-mono text-slate-300 mt-0.5">{cluster.k8s_version || "—"}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Nodes</p>
          <p className="text-sm font-mono text-slate-300 mt-0.5">{cluster.nodes_total ?? "—"}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Namespaces</p>
          <p className="text-sm font-mono text-slate-300 mt-0.5">{cluster.namespaces_count ?? "—"}</p>
        </div>
      </div>

      {cluster.error && (
        <div className="mt-3 px-3 py-2 rounded bg-amber-400/5 border border-amber-400/15 text-[10px] text-amber-300 font-mono break-all">
          {cluster.error}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800/60">
        <button
          data-testid="refresh-cluster-btn"
          onClick={() => onRefresh(cluster.id)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          data-testid="delete-cluster-btn"
          onClick={() => onDelete(cluster.id)}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-slate-700 text-rose-400 hover:bg-rose-400/10 transition-colors ml-auto"
        >
          <Trash2 size={11} /> Remove
        </button>
      </div>
    </div>
  );
}

export default function ConnectCluster() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/user/clusters`, { withCredentials: true });
      setClusters(data);
    } catch (e) {
      setError("Failed to load clusters");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    setError(null); setSuccess(null);
    if (!file) return;
    if (file.size > 200_000) {
      setError("File too large (max 200KB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/clusters/upload-kubeconfig`, form, { withCredentials: true });
      setSuccess(`Uploaded "${data.name}" — ${data.reachable ? "Connected" : "Unreachable"}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleRefresh = async (id) => {
    setRefreshingId(id);
    try {
      await axios.post(`${API}/user/clusters/${id}/refresh`, {}, { withCredentials: true });
      load();
    } catch (e) {
      setError("Refresh failed");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this cluster?")) return;
    try {
      await axios.delete(`${API}/user/clusters/${id}`, { withCredentials: true });
      load();
    } catch (e) {
      setError("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Connect a Cluster
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Upload your <span className="font-mono text-slate-300">kubeconfig</span> file to connect a real Kubernetes cluster. Stored encrypted, never logged.
        </p>
      </div>

      {/* Upload dropzone */}
      <div
        data-testid="kubeconfig-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-md p-10 text-center transition-all",
          dragOver ? "border-indigo-400 bg-indigo-400/5" : "border-slate-700 bg-slate-900/30 hover:border-slate-600"
        )}
      >
        <Upload size={28} className={cn("mx-auto mb-3", dragOver ? "text-indigo-400" : "text-slate-500")} />
        <p className="text-sm text-slate-300 mb-1">
          {uploading ? "Uploading & verifying…" : "Drop your kubeconfig file here"}
        </p>
        <p className="text-xs text-slate-500 mb-4">YAML format · Max 200KB</p>
        <label className={cn(
          "inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold cursor-pointer transition-colors",
          uploading ? "bg-slate-800 text-slate-500" : "bg-indigo-500 hover:bg-indigo-400 text-white"
        )}>
          {uploading ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Uploading…" : "Choose file"}
          <input
            data-testid="kubeconfig-file-input"
            type="file"
            accept=".yaml,.yml,.kubeconfig,application/x-yaml,text/yaml,text/plain"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      </div>

      {error && (
        <div data-testid="upload-error" className="px-4 py-2.5 rounded bg-rose-400/10 border border-rose-400/20 text-rose-400 text-sm flex items-start gap-2">
          <X size={14} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="upload-success" className="px-4 py-2.5 rounded bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-sm flex items-start gap-2">
          <CheckCircle size={14} className="mt-0.5 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Connected clusters list */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Layers size={14} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Your Connected Clusters
          </h3>
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
            {clusters.length}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-40 bg-slate-800/40 rounded-md animate-pulse" />
            ))}
          </div>
        ) : clusters.length === 0 ? (
          <div className="bg-slate-900/30 border border-slate-800 rounded-md p-8 text-center">
            <FileText size={20} className="mx-auto mb-2 text-slate-600" />
            <p className="text-sm text-slate-400">No clusters connected yet</p>
            <p className="text-xs text-slate-600 mt-1">Upload a kubeconfig above to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {clusters.map((c) => (
              <ClusterCard
                key={c.id}
                cluster={c}
                onRefresh={handleRefresh}
                onDelete={handleDelete}
                refreshing={refreshingId === c.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
